// client/src/coms/components/mobile/MobileVoiceDock.js
// Phones: while you are in a voice channel, a slim bar above the bottom navigation keeps the essential voice
// controls (mute, deafen, leave) and the channel one tap away, whatever screen you are on. The full voice bar in
// the channel drawer is unchanged; both call the same voice session actions.
import React from 'react';

const LS_VOICE_MUTED = 'vox:voiceMutedPref';
const LS_VOICE_DEAFENED = 'vox:voiceDeafenedPref';

function remember(key, value) {
  try {
    window.localStorage.setItem(key, value ? '1' : '0');
  } catch {}
}

export function voiceDockState(voiceSession) {
  const status = String((voiceSession && voiceSession.status) || '').toLowerCase();
  const target = !!(voiceSession && String(voiceSession.serverId || '').trim() && String(voiceSession.channelId || '').trim());
  const inVoice = ['joining', 'connecting', 'connected', 'reconnecting'].includes(status) || target;
  const label =
    status === 'joining' || status === 'connecting'
      ? 'Connecting…'
      : status === 'reconnecting' || (voiceSession && voiceSession.connection === 'reconnecting')
        ? 'Reconnecting…'
        : status === 'error'
          ? 'Not connected'
          : 'In voice';
  return { inVoice, label, status };
}

export default function MobileVoiceDock({ voiceSession, channelName, serverName, onOpenChannel, onToggleMute, onToggleDeafen, onLeave }) {
  const { inVoice, label, status } = voiceDockState(voiceSession);
  if (!inVoice) return null;
  const muted = !!(voiceSession && voiceSession.muted);
  const deafened = !!(voiceSession && voiceSession.deafened);
  return (
    <div className={`vx-vdock${status === 'reconnecting' ? ' is-warn' : ''}`} role="region" aria-label="Voice controls" data-testid="voice-dock">
      <button type="button" className="vx-vdock-where" onClick={onOpenChannel} aria-label={`Open ${channelName || 'voice channel'}`}>
        <span className="vx-vdock-dot" aria-hidden="true" />
        <span className="vx-vdock-text">
          <span className="vx-vdock-status">{label}</span>
          <span className="vx-vdock-name">
            🔊 {channelName || 'Voice'}
            {serverName ? <span className="vx-vdock-server"> · {serverName}</span> : null}
          </span>
        </span>
      </button>
      <button
        type="button"
        className={`vx-vdock-btn${muted ? ' is-on' : ''}`}
        aria-pressed={muted}
        aria-label={muted ? 'Unmute microphone' : 'Mute microphone'}
        onClick={() => {
          remember(LS_VOICE_MUTED, !muted);
          onToggleMute();
        }}
        data-testid="voice-dock-mute"
      >
        {muted ? '🔇' : '🎙️'}
      </button>
      <button
        type="button"
        className={`vx-vdock-btn${deafened ? ' is-on' : ''}`}
        aria-pressed={deafened}
        aria-label={deafened ? 'Undeafen' : 'Deafen'}
        onClick={() => {
          remember(LS_VOICE_DEAFENED, !deafened);
          onToggleDeafen();
        }}
        data-testid="voice-dock-deafen"
      >
        {deafened ? '🔕' : '🎧'}
      </button>
      <button type="button" className="vx-vdock-btn is-danger" aria-label="Leave voice" onClick={onLeave} data-testid="voice-dock-leave">
        ⏻
      </button>
    </div>
  );
}
